import { useState, useEffect, useRef } from 'react'

const MOBILE_QUERY = '(max-width: 991px)'

function htmlMarkup(html) {
  return { __html: html || '' }
}

function CartBadge({ count }) {
  return <span className="site-navbar__cart-count">{count || 0}</span>
}

function Branding({ branding }) {
  if (branding?.hasCustomLogo && branding.logoHtml) {
    return <div className="site-branding" dangerouslySetInnerHTML={htmlMarkup(branding.logoHtml)} />
  }

  const TitleTag = branding?.isHomeHeading ? 'h1' : 'p'
  const showDescription = Boolean(branding?.description || branding?.isCustomizer)

  return (
    <div className="site-branding">
      <div className="site-branding__text">
        <TitleTag className="site-title">
          <a href={branding?.homeUrl || '/'}>{branding?.siteName || ''}</a>
        </TitleTag>

        {showDescription && <p className="site-description">{branding.description}</p>}
      </div>
    </div>
  )
}

function BurgerIcon({ isOpen }) {
  return (
    <span className={`nova-burger${isOpen ? ' nova-burger--open' : ''}`} aria-hidden="true">
      <span className="nova-burger__bar" />
      <span className="nova-burger__bar" />
      <span className="nova-burger__bar" />
    </span>
  )
}

function IconButtonContent({ iconClass, label }) {
  return (
    <>
      <i className={iconClass} aria-hidden="true" />
      <span className="screen-reader-text">{label}</span>
    </>
  )
}

export function Navbar({ payload }) {
  // Track the mobile breakpoint so the drawer chrome is only ever rendered on
  // mobile — on desktop none of it exists in the DOM, keeping the desktop
  // navbar byte-for-byte identical to the classic layout.
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_QUERY).matches
  )
  const [isOpen, setIsOpen] = useState(false)
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const wrapperRef = useRef(null)
  const toggleRef = useRef(null)
  const panelRef = useRef(null)
  const searchGroupRef = useRef(null)
  const closeButtonRef = useRef(null)

  const branding = payload.branding || {}
  const labels = payload.labels || {}
  const settings = payload.settings || {}
  const urls = payload.urls || {}
  const showInlineCart = settings.showCart && !settings.stickyCart
  const drawerOpen = isMobile && isOpen

  const close = () => {
    setIsOpen(false)
    setIsSearchOpen(false)
  }
  const toggle = () => {
    if (isOpen) {
      setIsSearchOpen(false)
    }

    setIsOpen((prev) => !prev)
  }
  const toggleSearch = () => setIsSearchOpen((prev) => !prev)

  // Subscribe to the breakpoint, and force the drawer shut when leaving mobile.
  useEffect(() => {
    const mql = window.matchMedia(MOBILE_QUERY)
    const onChange = (event) => {
      setIsMobile(event.matches)
      if (!event.matches) {
        setIsOpen(false)
        setIsSearchOpen(false)
      }
    }
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [])

  // Lock the body scroll without shifting the page, sync the header state, and
  // tidy up any submenu / inline-search state when the drawer closes.
  useEffect(() => {
    const header = wrapperRef.current?.closest('.site-header')
    const isDrawerOpen = isMobile && isOpen

    if (header) {
      header.classList.toggle('is-menu-open', isDrawerOpen)
    }

    if (isDrawerOpen) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
      document.body.style.overflow = 'hidden'
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`
      }
      closeButtonRef.current?.focus()
    } else {
      document.body.style.overflow = ''
      document.body.style.paddingRight = ''

      if (header) {
        header
          .querySelectorAll('.menu-item-has-mega-menu.is-submenu-open, .menu-item-has-children.is-submenu-open')
          .forEach((item) => {
            item.classList.remove('is-submenu-open')
            const link = item.querySelector(':scope > a')
            if (link) link.setAttribute('aria-expanded', 'false')
          })

        const searchPanel = header.querySelector('[data-navbar-search]')
        const searchToggle = header.querySelector('[data-navbar-search-toggle]')
        const searchGroup = header.querySelector('.site-navbar__search-group')
        if (searchPanel) searchPanel.setAttribute('hidden', 'hidden')
        if (searchToggle) searchToggle.setAttribute('aria-expanded', 'false')
        if (searchGroup) searchGroup.classList.remove('is-open')
      }
    }

    return () => {
      document.body.style.overflow = ''
      document.body.style.paddingRight = ''
      if (header) header.classList.remove('is-menu-open')
    }
  }, [isMobile, isOpen])

  // Close on Escape.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        close()
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])

  useEffect(() => {
    if (!isSearchOpen) return undefined

    const focusSearchInput = () => {
      const input = searchGroupRef.current?.querySelector('.search-field, input[type="search"], input[name="s"]')
      input?.focus()
    }

    const animationFrame = window.requestAnimationFrame(focusSearchInput)
    return () => window.cancelAnimationFrame(animationFrame)
  }, [isSearchOpen])

  useEffect(() => {
    if (!isSearchOpen) return undefined

    const handleSearchOutsidePointerDown = (event) => {
      if (searchGroupRef.current?.contains(event.target)) {
        return
      }

      setIsSearchOpen(false)
    }

    document.addEventListener('pointerdown', handleSearchOutsidePointerDown, true)
    return () => document.removeEventListener('pointerdown', handleSearchOutsidePointerDown, true)
  }, [isSearchOpen])

  useEffect(() => {
    if (!drawerOpen) return undefined

    const handleOutsidePointerDown = (event) => {
      const target = event.target

      if (panelRef.current?.contains(target) || toggleRef.current?.contains(target)) {
        return
      }

      close()
    }

    document.addEventListener('pointerdown', handleOutsidePointerDown, true)
    return () => document.removeEventListener('pointerdown', handleOutsidePointerDown, true)
  }, [drawerOpen])

  // Inside the drawer, follow leaf links normally but let parent items keep the
  // drawer open while their submenu toggles (handled by the vanilla script).
  const handlePanelClick = (event) => {
    if (!isMobile) return
    const link = event.target.closest('a')
    if (!link) return

    const parentItem = link.closest('.menu-item-has-children, .menu-item-has-mega-menu')
    const isParentToggle = parentItem && parentItem.querySelector(':scope > a') === link
    if (isParentToggle) return

    close()
  }

  return (
    <div className="site-navbar" ref={wrapperRef}>
      <div className="site-navbar__inner">
        <Branding branding={branding} />

        <button
          type="button"
          className="site-navbar__toggle"
          ref={toggleRef}
          aria-expanded={drawerOpen ? 'true' : 'false'}
          aria-controls="site-navbar-panel"
          onClick={toggle}
        >
          <BurgerIcon isOpen={drawerOpen} />
          <span className="screen-reader-text">
            {drawerOpen ? labels.closeMenu || 'Fermer le menu' : labels.openMenu || 'Ouvrir le menu principal'}
          </span>
        </button>

        {showInlineCart && (
          <a className="site-navbar__mobile-cart site-navbar__icon-button site-navbar__icon-button--cart" href={urls.cart}>
            <IconButtonContent iconClass="bi bi-bag" label={labels.cart || 'Panier'} />
            <CartBadge count={payload.cartCount} />
          </a>
        )}

        {settings.showAccount && (
          <a
            className="site-navbar__mobile-account site-navbar__icon-button site-navbar__icon-button--account"
            href={urls.account}
          >
            <IconButtonContent iconClass="bi bi-person" label={labels.account || 'Mon compte'} />
          </a>
        )}

        {isMobile && (
          <div
            className={`site-navbar__backdrop${drawerOpen ? ' is-visible' : ''}`}
            onClick={close}
            aria-hidden="true"
          />
        )}

        <div
          className={`site-navbar__panel${drawerOpen ? ' is-open' : ''}`}
          id="site-navbar-panel"
          ref={panelRef}
          data-navbar-panel
          aria-hidden={isMobile && !isOpen ? 'true' : undefined}
          onClick={handlePanelClick}
        >
          {isMobile && (
            <div className="site-navbar__panel-header">
              <a href={branding?.homeUrl || '/'} className="site-navbar__panel-brand">
                {branding?.hasCustomLogo && branding.logoHtml ? (
                  <span dangerouslySetInnerHTML={htmlMarkup(branding.logoHtml)} />
                ) : (
                  <span className="site-navbar__panel-brand-name">{branding?.siteName || ''}</span>
                )}
              </a>
              <button
                type="button"
                className="site-navbar__panel-close"
                onClick={close}
                ref={closeButtonRef}
                aria-label={labels.closeMenu || 'Fermer le menu'}
              >
                <i className="bi bi-x-lg" aria-hidden="true" />
              </button>
            </div>
          )}

          <nav
            className="site-navigation"
            aria-label={labels.mainMenu || 'Menu principal'}
            dangerouslySetInnerHTML={htmlMarkup(payload.menuHtml)}
          />

          <div className="site-navbar__actions">
            {settings.showSearch && (
              <div className={`site-navbar__search-group${isSearchOpen ? ' is-open' : ''}`} ref={searchGroupRef}>
                <button
                  type="button"
                  className="site-navbar__icon-button site-navbar__search-toggle"
                  data-navbar-search-toggle
                  aria-expanded={isSearchOpen ? 'true' : 'false'}
                  aria-controls="site-navbar-search"
                  onClick={toggleSearch}
                >
                  <IconButtonContent
                    iconClass={isSearchOpen ? 'bi bi-x-lg' : 'bi bi-search'}
                    label={isSearchOpen ? labels.closeSearch || 'Fermer la recherche' : labels.showSearch || 'Afficher la recherche'}
                  />
                </button>

                <div
                  className="site-navbar__search"
                  id="site-navbar-search"
                  data-navbar-search
                  hidden={!isSearchOpen}
                  aria-hidden={isSearchOpen ? 'false' : 'true'}
                  dangerouslySetInnerHTML={htmlMarkup(payload.searchFormHtml)}
                />
              </div>
            )}

            {settings.showAccount && (
              <a className="site-navbar__icon-button site-navbar__icon-button--account" href={urls.account}>
                <IconButtonContent iconClass="bi bi-person" label={labels.account || 'Mon compte'} />
              </a>
            )}

            {showInlineCart && (
              <a className="site-navbar__icon-button site-navbar__icon-button--cart" href={urls.cart}>
                <IconButtonContent iconClass="bi bi-bag" label={labels.cart || 'Panier'} />
                <CartBadge count={payload.cartCount} />
              </a>
            )}

            {settings.buttonLabel && settings.buttonUrl && (
              <a className="site-navbar__cta" href={settings.buttonUrl}>
                {settings.buttonLabel}
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
